import React, { useEffect, useState } from "react"
import { Modal } from "react-bootstrap"
import { Form } from "react-bootstrap"
import { useFetchTags } from "../hooks/useFetchTags"
import { useAddVesselLog } from "../hooks/useAddVesselLog"
import { addVesselLogParams } from "../hooks/useAddVesselLog"
import { useFetchLogs } from "../hooks/useFetchLogs"
import { toast } from "react-toastify"
import { useSelector } from "react-redux"
import { RootState } from "store"
import { useWoopraAPI } from "instrumentation/tracking/useWoopraApi"

interface PrevetTagVesselModalProps {
  show: boolean
  handleClose: () => void
  vesselImo: string | null
  vesselSegment: string | null
}

function PrevetTagVesselModal({
  show,
  handleClose,
  vesselImo,
  vesselSegment,
}: PrevetTagVesselModalProps) {
  const { postTrackingEvent } = useWoopraAPI()
  const { tags, fetchTags, isFetchTagsLoading } = useFetchTags()
  const [activeTag, setActiveTag] = useState<string>()
  const [comment, setComment] = useState<string>("")
  const { createVesselLog, isAddCommentsLoading } = useAddVesselLog()
  const { vesselLogs, fetchLogs, isFetchVesselLogsLoading } = useFetchLogs()

  const chosenOrganisation = useSelector(
    (state: RootState) => state.user.chosenOrganization
  )

  //fetch tags and logs when modal is opened
  useEffect(() => {
    if (vesselImo && chosenOrganisation && show) {
      fetchTags()
      fetchLogs({
        imo: Number(vesselImo),
        orgId: chosenOrganisation.id,
      })
      setActiveTag(undefined)
      setComment("")
    }
  }, [vesselImo, show])

  //function to handle adding vessel log
  const handleAddVesselLog = async () => {
    if (!activeTag || !vesselImo) return

    const logObj: addVesselLogParams = {
      vesselImo,
      tagId: activeTag,
      comment,
    }
    try {
      await createVesselLog(logObj)
      toast.success("Successfully added vessel log", {
        position: "bottom-right",
        autoClose: 8000,
      })
    } catch (err: unknown) {
      toast.error("Unable to add vessel log", {
        position: "bottom-right",
        autoClose: 8000,
      })
    } finally {
      handleClose()
    }
  }

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Tag Vessel</Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 ">
        <p>Tag:</p>
        <div className="d-flex flex-wrap justify-content-between w-100 gap-3">
          {!isFetchTagsLoading ? (
            tags &&
            tags.map((tag) => {
              return (
                <button
                  key={tag.id}
                  className={`btn border flex-grow-1 ${
                    activeTag === tag.id && "btn-primary"
                  }`}
                  title={tag.tagName}
                  onClick={() => {
                    setActiveTag(tag.id)
                    postTrackingEvent({
                      page: "/prevetting",
                      event: "click_tag",
                      properties: {
                        vesselSegment: vesselSegment ? vesselSegment : "",
                        tag: tag.tagName,
                        component: "tag_vessel",
                      },
                    })
                  }}
                  style={{
                    minWidth: "150px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {tag.tagName}
                </button>
              )
            })
          ) : (
            <p>loading...</p>
          )}
        </div>
        <p className="mt-3 mb-1">Comment:</p>
        <Form.Control
          as="textarea"
          rows={3}
          className="w-100 mt-0"
          onChange={(e) => {
            setComment(e.target.value)
          }}
        />
        <div className="d-flex justify-content-end mt-2 gap-3">
          <button
            className="btn bg-dark text-white"
            onClick={() => {
              handleClose()
              postTrackingEvent({
                page: "/prevetting",
                event: "cancel_tag_vessel",
                properties: {
                  vesselSegment: vesselSegment ? vesselSegment : "",
                  component: "tag_vessel",
                },
              })
            }}
            style={{ width: "120px" }}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              handleAddVesselLog()
              postTrackingEvent({
                page: "/prevetting",
                event: "save_comment",
                properties: {
                  vesselSegment: vesselSegment ? vesselSegment : "",
                  component: "tag_vessel",
                },
              })
            }}
            style={{ width: "120px" }}
            disabled={isAddCommentsLoading || !activeTag}
          >
            Save
          </button>
        </div>
        <p>All Tags & Comment:</p>

        <div
          className="border p-2"
          style={{
            height: "180px",
            overflowY: "auto",
            cursor: "pointer",
          }}
        >
          {isFetchVesselLogsLoading ? (
            <p>Loading...</p>
          ) : vesselLogs && vesselLogs?.length > 0 ? (
            <table>
              <tbody>
                {vesselLogs.map((log, index) => {
                  return (
                    <tr key={log.id}>
                      <td>
                        {index + 1 + ". "}
                        {formatDates(log.createdAt)}{" "}
                      </td>
                      <td>{" (" + log.createdBy + ")"}</td>
                      <td className="ps-4">
                        <span className="fw-bold">
                          {" " + log.tagName + ": "}
                        </span>
                      </td>
                      <td className="ps-1">{log.comment}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <p>No logs for this vessel</p>
          )}
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default PrevetTagVesselModal

function formatDates(dateStr: string): string {
  const date = new Date(dateStr)

  const day = date.getDate()
  const month = date.toLocaleString("en-GB", { month: "short" }) // e.g. "Sept"
  const year = date.getFullYear()

  // ordinal suffix
  const suffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th"

  return `${day}${suffix} ${month}. ${year}`
}
